import React, { useState, useEffect, useCallback } from 'react';
import { Container, Box, Typography, Divider, Grid, Paper } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalculateIcon from '@mui/icons-material/Calculate';
import SecurityIcon from '@mui/icons-material/Security';
import SchoolIcon from '@mui/icons-material/School';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Helmet } from 'react-helmet';
import { useConfig } from '../../context/ConfigContext';
import { calculateComparison } from '../../calculations/comparisonCalculations';
import ComparisonInputs from './ComparisonInputs';
import QuickComparison from './QuickComparison';
import ComparisonResults from './ComparisonResults';
import ComparisonVisualization from './ComparisonVisualization';
import { WebApplicationSchema, BreadcrumbSchema } from '../StructuredData';

const SalaryComparison = () => {
  const { config } = useConfig();

  // State for input data from both salaries
  const [inputData, setInputData] = useState({
    salaryA: {
      salary: 40000,
      overtime: { include: false, value: 0, inputType: 'amount' },
      pension: {
        active: false,
        contribution: 5,
        inputType: 'percentage',
        scheme: 'Auto-enrolment',
        calculationBasis: 'Qualifying Earnings',
        includeOvertime: false,
      },
      studentLoanPlan: { plan: 'none' }
    },
    salaryB: {
      salary: 40000,
      overtime: { include: false, value: 0, inputType: 'amount' },
      pension: {
        active: false,
        contribution: 5,
        inputType: 'percentage',
        scheme: 'Auto-enrolment',
        calculationBasis: 'Qualifying Earnings',
        includeOvertime: false,
      },
      studentLoanPlan: { plan: 'none' }
    }
  });

  // State for comparison results
  const [comparisonData, setComparisonData] = useState(null);

  // Calculate comparison whenever input data changes
  useEffect(() => {
    if (!config) return; // Don't calculate until config is loaded
    
    const results = calculateComparison(inputData.salaryA, inputData.salaryB, config);
    setComparisonData(results);
  }, [inputData, config]);

  // Handle input changes from ComparisonInputs
  const handleInputChange = useCallback((newInputData) => {
    setInputData(newInputData);
  }, []);

  // Don't render until config is loaded
  if (!config) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h4">Loading...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Helmet>
        <title>Compare Salaries | UK Salary Comparison Calculator | SalaryTools.co.uk</title>
        <meta
          name="description"
          content="Compare two UK salaries side-by-side. Calculate take-home pay differences, tax breakdowns, and see which salary is better with our interactive comparison calculator."
        />
        <link rel="canonical" href="https://salarytools.co.uk/salary-comparison" />
      </Helmet>

      <WebApplicationSchema />
      <BreadcrumbSchema 
        items={[
          { name: "Home", url: "https://salarytools.co.uk" },
          { name: "Compare Salaries", url: "https://salarytools.co.uk/salary-comparison" }
        ]}
      />

      {/* Page Header */}
      <Box sx={{ pt: 8, mb: 6, textAlign: 'center' }}>
        <Typography variant="h1" component="h1" sx={{ 
          fontSize: { xs: '2.5rem', md: '3.5rem' },
          fontWeight: 'bold',
          mb: 2
        }}>
          Compare UK Salaries
        </Typography>
        <Typography variant="h5" component="h2" sx={{ 
          mb: 3,
          color: 'text.secondary',
          maxWidth: '800px',
          mx: 'auto'
        }}>
          Compare two salaries side-by-side with visual tax breakdowns, 
          take-home pay analysis, and comprehensive financial insights
        </Typography>
        <Typography variant="body1" sx={{ 
          maxWidth: '600px',
          mx: 'auto',
          color: 'text.secondary'
        }}>
          Perfect for job negotiations, career decisions, or understanding the true value 
          of different compensation packages including pensions, overtime, and student loans.
        </Typography>
      </Box>

      {/* Input Section */}
      <ComparisonInputs onInputChange={handleInputChange} />

      <Divider sx={{ my: 6 }} />

      {/* Results Section */}
      {comparisonData && (
        <>
          {/* Quick Comparison - First */}
          <QuickComparison comparisonData={comparisonData} />
          
          <Divider sx={{ my: 6 }} />
          
          {/* Detailed Results */}
          <Box id="detailed-results">
            <ComparisonResults comparisonData={comparisonData} />
          </Box>
          
          <Divider sx={{ my: 6 }} />
          
          {/* Visualization Section */}
          <ComparisonVisualization 
            comparisonData={comparisonData}
            config={config}
          />
        </>
      )}

      {/* About This Comparison Tool */}
      <Box sx={{ mt: 8, mb: 6 }}>
        <Typography variant="h4" component="h2" sx={{ 
          textAlign: 'center',
          fontWeight: 'bold',
          mb: 4
        }}>
          About This Comparison Tool
        </Typography>
        
        <Typography variant="body1" sx={{ 
          textAlign: 'center',
          maxWidth: '800px',
          mx: 'auto',
          mb: 4,
          fontSize: '1.1rem'
        }}>
          Our salary comparison tool uses the latest HMRC tax rates and National Insurance 
          thresholds to provide accurate, comprehensive calculations for informed career decisions.
        </Typography>

        <Grid container spacing={4} sx={{ mb: 4}}>
          <Grid size={{ xs: 12, md: 4 }} sx={{ mb: 6}}>
            <Paper elevation={0} sx={{ pt: 5, pl: 3, pr: 3, textAlign: 'center', border: '1px solid #e0e0e0', height: '100%' }}>
              <CalculateIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Income Tax Calculations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Accurate calculations across all UK tax brackets using current HMRC rates
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 4 }} sx={{ mb: 6}}>
            <Paper elevation={0} sx={{ pt: 5, pl: 3, pr: 3, textAlign: 'center', border: '1px solid #e0e0e0', height: '100%' }}>
              <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                National Insurance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Both employee contributions and thresholds included in all calculations
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 4 }} sx={{ mb: 6}}>
            <Paper elevation={0} sx={{ pt: 5, pl: 3, pr: 3, textAlign: 'center', border: '1px solid #e0e0e0', height: '100%' }}>
              <AccountBalanceIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Pension Options
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Auto-enrolment, salary sacrifice, and personal pension calculations
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 4 }} sx={{ mb: 6}}>
            <Paper elevation={0} sx={{ pt: 5, pl: 3, pr: 3, textAlign: 'center', border: '1px solid #e0e0e0', height: '100%' }}>
              <SchoolIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Student Loan Repayments
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All plan types supported (Plans 1, 2, 4, and Postgraduate)
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 4 }} sx={{ mb: 6}}>
            <Paper elevation={0} sx={{ pt: 5, pl: 3, pr: 3, textAlign: 'center', border: '1px solid #e0e0e0', height: '100%' }}>
              <AccessTimeIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Overtime Integration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Additional earnings seamlessly included in all tax calculations
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 4 }} sx={{ mb: 6}}>
            <Paper elevation={0} sx={{ pt: 5, pl: 3, pr: 3, textAlign: 'center', border: '1px solid #e0e0e0', height: '100%' }}>
              <LocationOnIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Scottish Tax Rates
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Specialized calculations when Scottish residency is selected
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper elevation={0} sx={{ 
          p: 4, 
          backgroundColor: '#f8f9fa', 
          border: '1px solid #e0e0e0', 
          textAlign: 'center',
          borderRadius: 1
        }}>
          <Typography variant="body2" sx={{ 
            fontStyle: 'italic',
            color: 'text.secondary'
          }}>
            <strong>Disclaimer:</strong> All calculations are based on the 2025-26 tax year rates and are for illustrative purposes only. 
            Always consult with a qualified financial advisor for personalized advice.
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default SalaryComparison;